import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Plus, Trash2, AlertCircle } from 'lucide-react';

/**
 * GSTRateConfig Component
 * 
 * Allows configuration of GST rates for subcategories with optional threshold-based slabs.
 * Supports both standard GST rates and custom rates (e.g., 3% for Jewelry).
 * 
 * Example use cases:
 * - Hotel rooms: 0% up to ₹1000, 5% up to ₹2500, 12% up to ₹7500, 18% above
 * - Jewelry: 3% flat rate
 * - Restaurants: 5% without AC, 18% with AC
 */

const GST_RATE_OPTIONS = [0, 3, 5, 12, 18, 28];
const MAX_SLABS = 4;

const GSTRateConfig = ({ value, onChange }) => {
  const [config, setConfig] = useState({
    has_threshold_rates: false,
    default_rate: 18,
    slabs: []
  });
  const [useCustomRate, setUseCustomRate] = useState(false);
  const [customRate, setCustomRate] = useState('');

  useEffect(() => {
    if (value) {
      const defaultRate = value.default_rate ?? 18;
      const isCustom = !GST_RATE_OPTIONS.includes(defaultRate);
      
      setConfig({
        has_threshold_rates: value.has_threshold_rates || false,
        default_rate: defaultRate,
        slabs: value.slabs || []
      });
      
      if (isCustom) {
        setUseCustomRate(true);
        setCustomRate(defaultRate.toString());
      }
    }
  }, [value]);

  const handleConfigChange = (newConfig) => {
    setConfig(newConfig);
    onChange(newConfig);
  };

  const toggleThresholdRates = (checked) => {
    handleConfigChange({
      ...config,
      has_threshold_rates: checked,
      slabs: checked && config.slabs.length === 0 
        ? [{ threshold: 1000, rate: 0 }] 
        : config.slabs
    });
  };

  const updateDefaultRate = (rate) => {
    setUseCustomRate(false);
    setCustomRate('');
    handleConfigChange({
      ...config,
      default_rate: parseFloat(rate)
    });
  };

  const handleCustomRateChange = (value) => {
    setCustomRate(value);
    const numRate = parseFloat(value);
    if (!isNaN(numRate) && numRate >= 0 && numRate <= 100) {
      handleConfigChange({
        ...config,
        default_rate: numRate
      });
    }
  };

  const toggleCustomRate = () => {
    if (useCustomRate) {
      // Switching back to standard rates
      setUseCustomRate(false);
      setCustomRate('');
      handleConfigChange({
        ...config,
        default_rate: 18
      });
    } else {
      // Switching to custom rate
      setUseCustomRate(true);
      setCustomRate(config.default_rate.toString());
    }
  };

  const addSlab = () => {
    if (config.slabs.length >= MAX_SLABS) return;
    
    const lastThreshold = config.slabs.length > 0 
      ? config.slabs[config.slabs.length - 1].threshold 
      : 0;
    
    handleConfigChange({
      ...config,
      slabs: [
        ...config.slabs,
        { threshold: lastThreshold + 1000, rate: 18 }
      ]
    });
  };

  const removeSlab = (index) => {
    handleConfigChange({
      ...config,
      slabs: config.slabs.filter((_, i) => i !== index)
    });
  };

  const updateSlab = (index, field, value) => {
    const newSlabs = [...config.slabs];
    newSlabs[index] = {
      ...newSlabs[index],
      [field]: field === 'threshold' ? parseFloat(value) || 0 : parseFloat(value)
    };
    
    // Sort by threshold after update
    newSlabs.sort((a, b) => a.threshold - b.threshold);
    
    handleConfigChange({
      ...config,
      slabs: newSlabs
    });
  };

  // Get all possible rate options including custom ones from slabs
  const getAllRateOptions = () => {
    const rates = new Set(GST_RATE_OPTIONS);
    config.slabs.forEach(slab => rates.add(slab.rate));
    if (customRate && !isNaN(parseFloat(customRate))) {
      rates.add(parseFloat(customRate));
    }
    return Array.from(rates).sort((a, b) => a - b);
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-gray-50" data-testid="gst-rate-config">
      {/* Default GST Rate */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Default GST Rate</Label>
        <div className="flex flex-wrap gap-2">
          {GST_RATE_OPTIONS.map((rate) => (
            <Button
              key={rate}
              type="button"
              variant={!useCustomRate && config.default_rate === rate ? "default" : "outline"}
              size="sm"
              onClick={() => updateDefaultRate(rate)}
              className="min-w-[50px]"
              data-testid={`gst-rate-${rate}`}
            >
              {rate}%
            </Button>
          ))}
          <Button
            type="button"
            variant={useCustomRate ? "default" : "outline"}
            size="sm"
            onClick={toggleCustomRate}
            className="min-w-[70px]"
            data-testid="gst-rate-custom"
          >
            Custom
          </Button>
        </div>
        
        {/* Custom Rate Input */}
        {useCustomRate && (
          <div className="flex items-center gap-2 mt-2">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={customRate}
              onChange={(e) => handleCustomRateChange(e.target.value)}
              placeholder="Enter rate"
              className="w-24"
              data-testid="custom-rate-input"
            />
            <span className="text-sm text-gray-500">%</span>
            <span className="text-xs text-gray-400">(e.g., 3% for Jewelry, 1.5% for Gold)</span>
          </div>
        )}
      </div>

      {/* Threshold Toggle */}
      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          id="has_threshold_rates"
          checked={config.has_threshold_rates}
          onCheckedChange={toggleThresholdRates}
          data-testid="threshold-rates-checkbox"
        />
        <div className="space-y-1">
          <Label htmlFor="has_threshold_rates" className="text-sm font-medium cursor-pointer">
            Enable threshold-based GST rates
          </Label>
          <p className="text-xs text-gray-500">
            Set different GST rates based on price ranges (e.g., hotel rooms under ₹1000 at 0%, above ₹7500 at 18%)
          </p>
        </div>
      </div>

      {/* Threshold Slabs */}
      {config.has_threshold_rates && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Price Slabs</Label>
            <Badge variant="outline" className="text-xs">
              {config.slabs.length}/{MAX_SLABS} slabs
            </Badge>
          </div>
          
          {config.slabs.length === 0 && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              No slabs configured. Add at least one slab.
            </div>
          )}

          <div className="space-y-2">
            {config.slabs.map((slab, index) => (
              <div 
                key={slab.id || slab.name || `${slab.title || "slab"}-${index}`} 
                className="flex items-center gap-3 p-3 bg-white border rounded-lg"
                data-testid={`gst-slab-${index}`}
              >
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">
                      {index === 0 ? 'Up to (₹)' : `From ₹${config.slabs[index-1].threshold.toLocaleString()} to (₹)`}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={slab.threshold}
                      onChange={(e) => updateSlab(index, 'threshold', e.target.value)}
                      placeholder="Price threshold"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">GST Rate (%)</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        value={getAllRateOptions().includes(slab.rate) ? slab.rate : 'custom'}
                        onChange={(e) => {
                          if (e.target.value !== 'custom') {
                            updateSlab(index, 'rate', e.target.value);
                          }
                        }}
                        className="flex-1 border rounded-md px-3 py-2 text-sm"
                      >
                        {getAllRateOptions().map((rate) => (
                          <option key={rate} value={rate}>{rate}%</option>
                        ))}
                        <option value="custom">Custom...</option>
                      </select>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={slab.rate}
                        onChange={(e) => updateSlab(index, 'rate', e.target.value)}
                        className="w-20"
                        placeholder="%"
                      />
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeSlab(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {config.slabs.length < MAX_SLABS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSlab}
              className="w-full"
              data-testid="add-slab-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Price Slab
            </Button>
          )}

          {/* Preview */}
          {config.slabs.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <Label className="text-xs font-medium text-blue-800">Rate Preview</Label>
              <div className="mt-2 space-y-1 text-sm text-blue-700">
                {config.slabs.map((slab, index) => {
                  const prevThreshold = index > 0 ? config.slabs[index - 1].threshold : 0;
                  return (
                    <div key={slab.id || slab.name || `${slab.title || "slab"}-${index}`} className="flex justify-between">
                      <span>
                        {index === 0 
                          ? `Up to ₹${slab.threshold.toLocaleString()}`
                          : `₹${prevThreshold.toLocaleString()} - ₹${slab.threshold.toLocaleString()}`
                        }
                      </span>
                      <Badge 
                        className={
                          slab.rate === 0 ? 'bg-green-100 text-green-800' :
                          slab.rate <= 5 ? 'bg-blue-100 text-blue-800' :
                          slab.rate <= 12 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }
                      >
                        {slab.rate}% GST
                      </Badge>
                    </div>
                  );
                })}
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span>Above ₹{config.slabs[config.slabs.length - 1]?.threshold.toLocaleString()}</span>
                  <Badge className="bg-orange-100 text-orange-800">
                    {config.default_rate}% GST
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Info about special rates */}
      <div className="text-xs text-gray-400 pt-2 border-t">
        <span className="font-medium">Common special rates:</span> Jewelry (3%), Gold (3%), Silver (3%), Diamonds (1.5%), Rough Diamonds (0.25%)
      </div>
    </div>
  );
};

export default GSTRateConfig;
